import { cloneElement, createContext, FC, isValidElement, ReactElement, ReactNode, useContext, useId } from 'react';

export type FieldContextValue = {
  /** The id the control received (generated when the Field had none). */
  id: string;
  /** Space-separated ids of the description and error, when present. */
  describedBy?: string;
  invalid: boolean;
  group: boolean;
  /** The id of the label element, so a control can reference it. */
  labelId: string;
};

// Context and its reader are co-located with Field, like CashierContext is with
// Cashier; splitting them out would be a larger refactor.
// eslint-disable-next-line react-refresh/only-export-components
export const FieldContext = createContext<FieldContextValue | null>(null);

/** Read the wiring of the enclosing Field. */
// eslint-disable-next-line react-refresh/only-export-components
export function useField(): FieldContextValue {
  const value = useContext(FieldContext);
  if (!value) {
    throw new Error('useField must be used inside a <Field>.');
  }
  return value;
}

export type FieldProps = {
  label: string;
  /** Ties the label, description and error to the control. Generated when omitted. */
  id?: string;
  description?: ReactNode;
  error?: ReactNode;
  /** Composite controls (thumbnail + buttons) have no single focusable control. */
  group?: boolean;
  /** Grid placement: take the whole row of a FormGrid. */
  span?: 'full';
  children: ReactNode;
  /** Placement only. */
  className?: string;
};

/**
 * A labelled control with its description and error.
 *
 * The wiring lives here rather than in every page: the control gets the id, and
 * aria-describedby / aria-invalid are added to it, so the association cannot be
 * forgotten. A group has no single control, so the wrapper itself carries
 * role="group" and aria-labelledby, naming the group without claiming the
 * child. Reserved space for the error keeps the layout still when one appears.
 */
const Field: FC<FieldProps> = ({ label, id, description, error, group = false, span, children, className }) => {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const labelId = fieldId + '-label';
  const describedBy = [description ? fieldId + '-desc' : null, error ? fieldId + '-err' : null].filter(Boolean).join(' ') || undefined;
  const invalid = Boolean(error);
  const wiring: FieldContextValue = { id: fieldId, describedBy, invalid, group, labelId };

  // A group has no single control, so the wrapper names itself instead: it
  // carries role="group" and points at the label, and the child is left exactly
  // as the page wrote it. A single control gets the id and ARIA wiring cloned
  // onto it.
  const control =
    !group && isValidElement(children)
      ? cloneElement(children as ReactElement<Record<string, unknown>>, {
          id: fieldId,
          'aria-describedby': describedBy,
          'aria-invalid': invalid || undefined,
        })
      : children;

  return (
    <FieldContext.Provider value={wiring}>
      <div
        className={['ui-field', span === 'full' && 'ui-field--full', className].filter(Boolean).join(' ')}
        role={group ? 'group' : undefined}
        aria-labelledby={group ? labelId : undefined}
        aria-describedby={group ? describedBy : undefined}
      >
        <label className="ui-field-label" id={labelId} htmlFor={group ? undefined : fieldId}>
          {label}
        </label>
        {control}
        {description && (
          <p className="ui-field-desc" id={fieldId + '-desc'}>
            {description}
          </p>
        )}
        {error && (
          <p className="ui-field-error" id={fieldId + '-err'} role="alert">
            {error}
          </p>
        )}
      </div>
    </FieldContext.Provider>
  );
};

export default Field;
